import { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle, Search, Mail, Phone } from "lucide-react";

export const Faq = () => {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const toggleFaq = (index: string) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      category: "Orders & Shipping",
      questions: [
        {
          question: "How can I track my order?",
          answer:
            "You can track your order by logging into your account and visiting the 'Order History' section. Alternatively, you can use the tracking number provided in your shipping confirmation email.",
        },
        {
          question: "What shipping methods do you offer?",
          answer:
            "We offer standard shipping (3-5 business days), express shipping (1-2 business days), and international shipping (7-14 business days) to most countries. Shipping methods and timeframes may vary based on your location.",
        },
        {
          question: "Do you ship internationally?",
          answer:
            "Yes, we ship to most countries worldwide. International shipping typically takes 7-14 business days, depending on the destination and customs processing.",
        },
      ],
    },
    {
      category: "Returns & Refunds",
      questions: [
        {
          question: "What is your return policy?",
          answer:
            "We offer a 30-day return policy for most items. Products must be in their original condition with tags attached. Custom or personalized items cannot be returned unless defective.",
        },
        {
          question: "How do I initiate a return?",
          answer:
            "To initiate a return, log into your account, find the order you wish to return, and click 'Return Items'. Follow the instructions to generate a return label and tracking number.",
        },
        {
          question: "When will I receive my refund?",
          answer:
            "Refunds are typically processed within 3-5 business days after we receive your returned items. The funds may take an additional 2-5 business days to appear in your account, depending on your bank or payment provider.",
        },
      ],
    },
    {
      category: "Product Information",
      questions: [
        {
          question: "Are your products sustainably sourced?",
          answer:
            "Yes, sustainability is one of our core values. We work with suppliers who adhere to ethical manufacturing practices, and many of our products are made from recycled or sustainably sourced materials.",
        },
        {
          question: "Do you offer size guides?",
          answer:
            "Yes, each product that comes in different sizes has a size guide on its product page. You can find detailed measurements and fit recommendations to help you choose the right size.",
        },
        {
          question: "How can I care for my products?",
          answer:
            "Care instructions are included with each product and can also be found on the product page. We recommend following these specific instructions to ensure the longevity of your items.",
        },
      ],
    },
    {
      category: "Account & Payment",
      questions: [
        {
          question: "How can I reset my password?",
          answer:
            "To reset your password, click on 'Login' and then 'Forgot Password'. Enter your email address, and we'll send you instructions to create a new password.",
        },
        {
          question: "What payment methods do you accept?",
          answer:
            "We accept Visa, Mastercard, Verve and Bank Transfer. All transactions are secured with industry-standard encryption.",
        },
        {
          question: "Is my payment information secure?",
          answer:
            "Absolutely. We use industry-leading encryption and security measures to protect your payment information. We are PCI DSS compliant and never store your full credit card details on our servers.",
        },
      ],
    },
  ];

  const filteredCategories = faqCategories
    .map((category) => {
      const filteredQuestions = category.questions.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      return filteredQuestions.length ? { ...category, questions: filteredQuestions } : null;
    })
    .filter(Boolean) as typeof faqCategories;

  return (
    <div className='bg-white min-h-screen'>
      {/* Hero Section */}
      <div className='bg-primary text-white py-20'>
        <div className='container mx-auto px-4 md:px-8 text-center'>
          <HelpCircle size={64} className='mx-auto mb-6' />
          <h1 className='text-4xl md:text-5xl font-bold mb-4'>Frequently Asked Questions</h1>
          <p className='text-xl max-w-2xl mx-auto'>
            Find answers to common questions about our products, shipping, returns, and more.
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className='container mx-auto px-4 md:px-8 py-10'>
        <div className='max-w-2xl mx-auto relative'>
          <input
            type='text'
            placeholder='Search for answers...'
            className='w-full py-3 px-12 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search
            className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400'
            size={20}
          />
        </div>
      </div>

      {/* FAQ Categories */}
      <div className='container mx-auto px-4 md:px-8 py-10'>
        {filteredCategories.length === 0 ? (
          <p className='text-center text-gray-500'>No results found for "{searchQuery}"</p>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
            {filteredCategories.map((category, catIndex) => (
              <div key={catIndex} className='bg-gray-50 rounded-lg p-6'>
                <h2 className='text-2xl font-bold text-primary mb-6'>{category.category}</h2>
                <div className='space-y-4'>
                  {category.questions.map((faq, faqIndex) => {
                    const index = `${catIndex}-${faqIndex}`;
                    return (
                      <div
                        key={faqIndex}
                        className='border border-gray-200 rounded-lg bg-white overflow-hidden'
                      >
                        <button
                          className='w-full flex justify-between items-center p-4 text-left focus:outline-none'
                          onClick={() => toggleFaq(index)}
                        >
                          <span className='font-medium text-lg'>{faq.question}</span>
                          {openIndex === index ? (
                            <ChevronUp className='text-primary' size={20} />
                          ) : (
                            <ChevronDown className='text-gray-500' size={20} />
                          )}
                        </button>
                        {openIndex === index && (
                          <div className='p-4 pt-0 border-t border-gray-100'>
                            <p className='text-gray-600'>{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Still Have Questions */}
      <div className='bg-gray-50 py-16 mt-10'>
        <div className='container mx-auto px-4 md:px-8 text-center'>
          <h2 className='text-3xl font-bold text-primary mb-6'>Still Have Questions?</h2>
          <p className='text-gray-700 text-lg mb-10 max-w-2xl mx-auto'>
            We're here to help! Reach out to our customer support team through any of these
            channels.
          </p>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto'>
            <div className='bg-white p-6 rounded-lg shadow-md'>
              <Mail size={32} className='text-primary mx-auto mb-4' />
              <h3 className='text-xl font-bold mb-2'>Email Support</h3>
              <p className='text-gray-600 mb-4'>Get a response within 24 hours</p>
              <a
                href='mailto:support@yourcompany.com'
                className='text-secondary font-medium hover:underline'
              >
                support@stedshop.com
              </a>
            </div>

            <div className='bg-white p-6 rounded-lg shadow-md'>
              <Phone size={32} className='text-primary mx-auto mb-4' />
              <h3 className='text-xl font-bold mb-2'>Phone Support</h3>
              <p className='text-gray-600 mb-4'>Available Mon-Fri, 9am-5pm</p>
              <a href='tel:+18001234567' className='text-secondary font-medium hover:underline'>
                09021872312
              </a>
            </div>

            <div className='bg-white p-6 rounded-lg shadow-md md:col-span-2 lg:col-span-1'>
              <HelpCircle size={32} className='text-primary mx-auto mb-4' />
              <h3 className='text-xl font-bold mb-2'>Help Center</h3>
              <p className='text-gray-600 mb-4'>Browse our detailed guides</p>
              <button className='text-secondary font-medium hover:underline'>
                Visit Help Center
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
